import React, { useCallback, useContext, useEffect, useState } from 'react';
import Modal from '@atlaskit/modal-dialog';
import styled from 'styled-components';

import Button from 'kingpin/atoms/Button';
import Icon from 'kingpin/atoms/Icon';
import TextInput from 'kingpin/atoms/TextInput';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
import TextArea from 'components/Inputs/TextArea';
import { useIsCurrentUserFleetOpsStaff } from 'components/Common/FleetOpsStaffOnly';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import ToastContext from 'contexts/ToastContext';
import {
  InsightsMemoryEntry,
  MEMORY_ROOT,
  deleteInsightsMemory,
  listInsightsMemory,
  readInsightsMemory,
  writeInsightsMemory,
} from 'services/insightsMemory';

// Lets a user see and edit the durable notes the Insights agent keeps about
// them (view / edit-and-save / create / delete). FleetOps staff can point it at
// another user's memory for debugging via the staff-only target field — the
// server 403s that field for everyone else. Content is the same GCS-backed
// memory the agent reads at the start of each conversation.

const ContentDiv = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 420px;
`;

const Panes = styled.div`
  display: flex;
  gap: 16px;
  flex: 1;
  min-height: 320px;
`;

const ListPane = styled.div`
  width: 240px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-right: 1px solid var(--color-border-secondary);
  padding-right: 16px;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
`;

const FileRow = styled.button<{ isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  cursor: pointer;
  text-align: left;
  padding: 6px 8px;
  border-radius: 6px;
  background-color: ${(props) =>
    props.isSelected ? 'var(--color-bg-secondary)' : 'transparent'};

  &:hover {
    background-color: var(--color-bg-secondary);
  }
`;

const EditorPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const EditorTextArea = styled(TextArea)`
  flex: 1;
  min-height: 260px;
  font-family: monospace;
  font-size: 13px;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
`;

// A short, readable byte size for a file row (e.g. "820 B", "5.5 KB").
const formatBytes = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  return `${(bytes / 1024).toFixed(1)} KB`;
};

// The tail of a `/memories/...` path, for a compact row label.
const fileLabel = (path: string): string =>
  path.startsWith(`${MEMORY_ROOT}/`)
    ? path.slice(MEMORY_ROOT.length + 1)
    : path;

interface Selected {
  path: string;
  // Whether this file exists on the server yet (false for a just-created one).
  persisted: boolean;
}

const InsightsMemoryModal = ({ onClose }: { onClose: () => void }) => {
  const { api } = useContext(CloudFunctionClientContext);
  const { showToast } = useContext(ToastContext);
  const isStaff = useIsCurrentUserFleetOpsStaff();

  // Staff-only: a userId to inspect instead of the caller's own memory.
  const [targetUserId, setTargetUserId] = useState<string>('');
  const scope = targetUserId.trim()
    ? { targetUserId: targetUserId.trim() }
    : {};

  const [entries, setEntries] = useState<InsightsMemoryEntry[] | null>(null);
  const [isListing, setIsListing] = useState<boolean>(false);

  const [selected, setSelected] = useState<Selected | null>(null);
  const [content, setContent] = useState<string>('');
  const [savedContent, setSavedContent] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [newName, setNewName] = useState<string>('');

  const refresh = useCallback(async () => {
    setIsListing(true);
    const result = await listInsightsMemory(api, scope);
    setIsListing(false);
    setEntries(result);
    if (result === null) {
      showToast('Could not load memory. Please try again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, targetUserId]);

  // Load the list on open and whenever the staff target changes. Switching
  // target abandons any in-progress edit (it belonged to the other user).
  useEffect(() => {
    setSelected(null);
    setContent('');
    setSavedContent('');
    refresh();
  }, [refresh]);

  const openFile = async (entry: InsightsMemoryEntry) => {
    setSelected({ path: entry.path, persisted: true });
    const fileContent = await readInsightsMemory(api, {
      path: entry.path,
      ...scope,
    });
    const text = fileContent ?? '';
    setContent(text);
    setSavedContent(text);
  };

  const startNewFile = () => {
    const name = newName.trim();
    if (name === '') {
      return;
    }
    const path = `${MEMORY_ROOT}/${name}`;
    setSelected({ path, persisted: false });
    setContent('');
    setSavedContent('');
    setNewName('');
  };

  const isDirty = content !== savedContent;
  const canSave =
    selected !== null && !isSaving && (isDirty || !selected.persisted);

  const onSave = async () => {
    if (!selected) {
      return;
    }
    setIsSaving(true);
    const ok = await writeInsightsMemory(api, {
      path: selected.path,
      content,
      ...scope,
    });
    setIsSaving(false);
    if (ok) {
      setSelected({ path: selected.path, persisted: true });
      setSavedContent(content);
      showToast('Memory saved');
      refresh();
    } else {
      // Keep the modal and the typed text — never discard the user's edit.
      showToast('Could not save. Please try again.');
    }
  };

  const onDelete = async () => {
    if (!selected || !selected.persisted) {
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${fileLabel(selected.path)}?`)) {
      return;
    }
    const ok = await deleteInsightsMemory(api, {
      path: selected.path,
      ...scope,
    });
    if (ok) {
      setSelected(null);
      setContent('');
      setSavedContent('');
      showToast('Memory deleted');
      refresh();
    } else {
      showToast('Could not delete. Please try again.');
    }
  };

  const hasFiles = entries !== null && entries.length > 0;

  return (
    <Modal width="x-large" onClose={onClose} shouldScrollInViewport>
      <ContentDiv data-testid="insights-memory-modal">
        <div>
          <Typography.Header type="H4">
            What the assistant remembers
          </Typography.Header>
          <Typography.Body type="Body 13" color="var(--color-text-secondary)">
            These are the notes Insights keeps to personalise its answers. Edit
            or remove anything that is out of date.
          </Typography.Body>
        </div>

        {isStaff && (
          <Row centerAlign style={{ gap: 8 }}>
            <Typography.Body type="Label">
              View another user (staff)
            </Typography.Body>
            <TextInput
              value={targetUserId}
              onChange={(e) => setTargetUserId(e.target.value)}
              placeholder="userId — leave blank for your own"
              data-testid="insights-memory-target"
            />
          </Row>
        )}

        <Panes>
          <ListPane>
            <Row spaceBetween centerAlign>
              <Typography.Body type="Label">Files</Typography.Body>
            </Row>
            <FileList>
              {entries?.map((entry) => (
                <FileRow
                  key={entry.path}
                  isSelected={selected?.path === entry.path}
                  onClick={() => openFile(entry)}
                  data-testid="insights-memory-file"
                >
                  <Typography.Body type="Body 12">
                    {fileLabel(entry.path)}
                  </Typography.Body>
                  <Typography.Body
                    type="Body 12"
                    color="var(--color-text-secondary)"
                  >
                    {formatBytes(entry.sizeBytes)}
                  </Typography.Body>
                </FileRow>
              ))}
              {!isListing && !hasFiles && (
                <Typography.Body
                  type="Body 12"
                  color="var(--color-text-secondary)"
                >
                  No memory saved yet.
                </Typography.Body>
              )}
            </FileList>
            <Row centerAlign style={{ gap: 4 }}>
              <TextInput
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="new-file.md"
                data-testid="insights-memory-new-name"
              />
              <Button
                type="Ghost"
                size="Small"
                icon="create-file"
                onClick={startNewFile}
                testId="insights-memory-new"
              />
            </Row>
          </ListPane>

          <EditorPane>
            {selected ? (
              <>
                <Row spaceBetween centerAlign>
                  <Typography.Body type="Label">
                    {fileLabel(selected.path)}
                  </Typography.Body>
                  {selected.persisted && (
                    <Button
                      type="Ghost"
                      size="Small"
                      label="Delete"
                      onClick={onDelete}
                      testId="insights-memory-delete"
                    />
                  )}
                </Row>
                <EditorTextArea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  data-testid="insights-memory-editor"
                />
              </>
            ) : (
              <EmptyState>
                <Typography.Body
                  type="Body 13"
                  color="var(--color-text-secondary)"
                >
                  Select a file to view it, or create a new one.
                </Typography.Body>
              </EmptyState>
            )}
          </EditorPane>
        </Panes>

        <Row spaceBetween centerAlign>
          <Icon icon="database" width={16} height={16} />
          <Row centerAlign style={{ gap: 8 }}>
            <Button
              type="Tertiary"
              size="Small"
              label="Close"
              onClick={onClose}
            />
            <Button
              type="Primary"
              size="Small"
              label="Save"
              onClick={onSave}
              isDisabled={!canSave}
              isLoading={isSaving}
              testId="insights-memory-save"
            />
          </Row>
        </Row>
      </ContentDiv>
    </Modal>
  );
};

export default InsightsMemoryModal;
